import React, { useEffect, useMemo, useRef, useState } from "react";
import { MessageSquare, Send, X, LogOut, UserRound, PencilLine, PanelsTopLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { getHttpUrl } from "../../Utils/config";

const ACCESS_KEY = "cx2_widget_access";
const REFRESH_KEY = "cx2_widget_refresh";
const USER_KEY = "cx2_widget_user";

function WidgetApp() {
  const searchParams = new URLSearchParams(window.location.search);
  const hashQuery = window.location.hash.includes("?")
    ? window.location.hash.split("?")[1]
    : "";
  const hashParams = new URLSearchParams(hashQuery);
  const envSiteKey = process.env.REACT_APP_WIDGET_SITE_KEY || "";
  const siteKey = searchParams.get("site_key") || hashParams.get("site_key") || envSiteKey;
  const apiBase = getHttpUrl("").replace(/\/$/, "");
  const [mode, setMode] = useState("login");
  const [token, setToken] = useState(() => sessionStorage.getItem(ACCESS_KEY) || "");
  const [user, setUser] = useState(() => {
    const raw = sessionStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  });
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isRailOpen, setIsRailOpen] = useState(false);
  const [panelSide, setPanelSide] = useState("left");
  const [authForm, setAuthForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
  });
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const endRef = useRef(null);

  const authHeaders = useMemo(() => {
    const headers = {
      "Content-Type": "application/json",
      "X-Widget-Site-Key": siteKey,
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }, [siteKey, token]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!token || !siteKey) return;
    loadSessions();
  }, [token, siteKey]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (!event?.data || event.data.type !== "cx2-widget-layout") return;
      if (event.data.panelSide === "left" || event.data.panelSide === "right") {
        setPanelSide(event.data.panelSide);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    window.parent.postMessage({ type: "cx2-widget-ready" }, "*");
  }, []);

  useEffect(() => {
    const handleBlur = () => {
      window.parent.postMessage({ type: "cx2-widget-blur" }, "*");
    };
    window.addEventListener("blur", handleBlur);
    return () => window.removeEventListener("blur", handleBlur);
  }, []);


  const persistAuth = (payload) => {
    sessionStorage.setItem(ACCESS_KEY, payload.access);
    sessionStorage.setItem(REFRESH_KEY, payload.refresh);
    sessionStorage.setItem(USER_KEY, JSON.stringify(payload.user));
    setToken(payload.access);
    setUser(payload.user);
  };

  const apiFetch = async (path, options = {}) => {
    const res = await fetch(`${apiBase}${path}`, {
      ...options,
      headers: {
        ...authHeaders,
        ...(options.headers || {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || data.detail || "Request failed");
    }
    return data;
  };

  const loadSessions = async () => {
    try {
      const data = await apiFetch("/api/ai/widget/sessions/");
      setSessions(data);
      if (!activeSession && data.length > 0) {
        openSession(data[0]);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const openSession = async (session) => {
    setActiveSession(session);
    setError("");
    try {
      const data = await apiFetch(`/api/ai/widget/sessions/${session.id}/messages/`);
      setMessages(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const createSession = async () => {
    const session = await apiFetch("/api/ai/widget/sessions/", {
      method: "POST",
      body: JSON.stringify({}),
    });
    setSessions((prev) => [session, ...prev]);
    setActiveSession(session);
    setMessages([]);
    return session;
  };

  const handleAuth = async () => {
    if (!siteKey) {
      setError("Missing widget site key.");
      return;
    }

    setAuthLoading(true);
    setError("");
    try {
      const endpoint =
        mode === "register" ? "/api/ai/widget/auth/register/" : "/api/ai/widget/auth/login/";
      const payload =
        mode === "register"
          ? authForm
          : { email: authForm.email, password: authForm.password, site_key: siteKey };
      const data = await fetch(`${apiBase}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Widget-Site-Key": siteKey,
        },
        body: JSON.stringify({ ...payload, site_key: siteKey }),
      }).then(async (res) => {
        const parsed = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(parsed.error || parsed.detail || "Authentication failed");
        return parsed;
      });

      persistAuth(data);
      setMode("chat");
      setAuthForm({
        first_name: "",
        last_name: "",
        email: authForm.email,
        password: "",
        confirm_password: "",
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!draft.trim()) return;
    setLoading(true);
    setError("");

    const optimistic = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content: draft.trim(),
      timestamp: new Date().toISOString(),
    };
    const outgoing = draft.trim();
    setMessages((prev) => [...prev, optimistic]);
    setDraft("");

    try {
      let session = activeSession;
      if (!session) {
        session = await createSession();
      }

      const data = await apiFetch(`/api/ai/widget/sessions/${session.id}/messages/`, {
        method: "POST",
        body: JSON.stringify({ content: outgoing }),
      });

      const refreshedMessages = await apiFetch(`/api/ai/widget/sessions/${session.id}/messages/`);
      setMessages(refreshedMessages);
      setSessions((prev) =>
        prev.map((item) =>
          item.id === session.id ? { ...item, title: data.session_title || item.title } : item
        )
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(ACCESS_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(USER_KEY);
    setToken("");
    setUser(null);
    setSessions([]);
    setActiveSession(null);
    setMessages([]);
    setMode("login");
    setIsRailOpen(false);
  };

  const closeWidget = () => {
    window.parent.postMessage({ type: "cx2-widget-close" }, "*");
  };

  const formatTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,#f2f7ff,transparent_42%),linear-gradient(180deg,#f8fbff_0%,#eef3f9_100%)] p-4 text-slate-900">
        <div className="mx-auto flex h-[calc(100vh-2rem)] max-w-md flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_30px_80px_rgba(15,23,42,0.14)]">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-[0_12px_24px_rgba(37,99,235,0.25)]">
                <MessageSquare className="h-5 w-5" />
              </div>
              <div>
                <div className="font-SFPro text-lg font-semibold">CX2 Assistant</div>
                <div className="text-xs text-slate-500">Secure support chat</div>
              </div>
            </div>
            <button onClick={closeWidget} className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 px-5 py-6">
            <div className="mb-6">
              <div className="font-SFPro text-2xl font-semibold">
                {mode === "register" ? "Create account" : "Welcome back"}
              </div>
              <div className="mt-1 text-sm text-slate-500">
                {mode === "register"
                  ? "Sign up to keep your conversations synced between the widget and app."
                  : "Log in to continue your chatbot sessions anywhere."}
              </div>
            </div>

            <div className="space-y-3">
              {mode === "register" && (
                <>
                  <input
                    value={authForm.first_name}
                    onChange={(e) => setAuthForm((prev) => ({ ...prev, first_name: e.target.value }))}
                    placeholder="First name"
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:bg-white"
                  />
                  <input
                    value={authForm.last_name}
                    onChange={(e) => setAuthForm((prev) => ({ ...prev, last_name: e.target.value }))}
                    placeholder="Last name"
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:bg-white"
                  />
                </>
              )}
              <input
                value={authForm.email}
                onChange={(e) => setAuthForm((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="Email address"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:bg-white"
              />
              <input
                type="password"
                value={authForm.password}
                onChange={(e) => setAuthForm((prev) => ({ ...prev, password: e.target.value }))}
                placeholder="Password"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:bg-white"
              />
              {mode === "register" && (
                <input
                  type="password"
                  value={authForm.confirm_password}
                  onChange={(e) => setAuthForm((prev) => ({ ...prev, confirm_password: e.target.value }))}
                  placeholder="Confirm password"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:bg-white"
                />
              )}
            </div>

            {error && <div className="mt-4 rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

            <button
              onClick={handleAuth}
              disabled={authLoading}
              className="mt-6 w-full rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-[0_12px_24px_rgba(37,99,235,0.22)] transition hover:bg-blue-700 disabled:opacity-60"
            >
              {authLoading ? "Please wait..." : mode === "register" ? "Create account" : "Log in"}
            </button>

            <button
              onClick={() => {
                setMode((prev) => (prev === "register" ? "login" : "register"));
                setError("");
              }}
              className="mt-3 w-full text-sm text-slate-500 hover:text-slate-800"
            >
              {mode === "register" ? "Already have an account? Log in" : "Need an account? Sign up"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#eef4ff_0%,#f8fbff_26%,#ffffff_100%)] text-slate-900">
      <div className="relative mx-auto flex h-screen max-w-5xl overflow-hidden">
        <aside
          className={`absolute inset-y-0 z-20 w-[260px] overflow-hidden bg-[#f8fbff] transition-transform duration-200 ease-out ${
            panelSide === "right" ? "left-0 border-r border-slate-200" : "right-0 border-l border-slate-200"
          } ${
            isRailOpen
              ? "translate-x-0 shadow-[4px_0_24px_rgba(15,23,42,0.10)]"
              : panelSide === "right"
                ? "-translate-x-full"
                : "translate-x-full"
          }`}
        >
          <div className="flex h-full w-[260px] flex-col">
          <div className="border-b border-slate-200 px-5 py-5">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-white">
                <UserRound className="h-5 w-5" />
              </div>
              <div>
                <div className="font-SFPro text-base font-semibold">
                  {user?.first_name || user?.email}
                </div>
                <div className="text-xs text-slate-500">{user?.email}</div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between px-5 py-4">
            <div>
              <div className="font-SFPro text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                Sessions
              </div>
            </div>
            <button
              onClick={createSession}
              className="rounded-full bg-white p-2 text-slate-500 shadow-sm ring-1 ring-slate-200 hover:text-blue-600"
              title="Start new chat"
            >
              <PencilLine className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-3 pb-4">
            {sessions.map((session) => (
              <button
                key={session.id}
                onClick={() => openSession(session)}
                className={`mb-2 w-full rounded-2xl px-4 py-3 text-left transition ${
                  activeSession?.id === session.id
                    ? "bg-blue-600 text-white shadow-[0_14px_30px_rgba(37,99,235,0.22)]"
                    : "bg-white text-slate-700 ring-1 ring-slate-200 hover:ring-blue-200"
                }`}
              >
                <div className="font-SFPro text-sm font-semibold">
                  {session.title || "New conversation"}
                </div>
                <div className={`mt-1 text-xs ${activeSession?.id === session.id ? "text-blue-100" : "text-slate-500"}`}>
                  Started {new Date(session.started_at).toLocaleDateString()}
                </div>
              </button>
            ))}
          </div>

          <div className="border-t border-slate-200 p-4">
            <button onClick={logout} className="flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50">
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          </div>
          </div>
        </aside>

        <main className="absolute inset-0 z-10 flex flex-col bg-white" onClick={() => { if (isRailOpen) setIsRailOpen(false); }}>
          <div className="flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsRailOpen((prev) => !prev)}
                className={`group flex h-11 items-center gap-2 rounded-2xl border px-3 text-sm font-medium transition-all duration-200 ${
                  isRailOpen
                    ? "border-blue-200 bg-blue-50 text-blue-700 shadow-[0_10px_24px_rgba(37,99,235,0.12)]"
                    : "border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                }`}
                title={isRailOpen ? "Hide sessions" : "Show sessions"}
              >
                <PanelsTopLeft className="h-4 w-4" />
                <span className="hidden sm:inline">{isRailOpen ? "Hide chats" : "View chats"}</span>
                {panelSide === "right" ? (
                  isRailOpen ? <ChevronRight className="h-4 w-4 opacity-70" /> : <ChevronLeft className="h-4 w-4 opacity-70 group-hover:translate-x-[-1px] transition-transform" />
                ) : (
                  isRailOpen ? <ChevronLeft className="h-4 w-4 opacity-70" /> : <ChevronRight className="h-4 w-4 opacity-70 group-hover:translate-x-[1px] transition-transform" />
                )}
              </button>
              <div>
                <div className="font-SFPro text-lg font-semibold">{activeSession?.title || "Start a conversation"}</div>
              <div className="text-sm text-slate-500">
                {activeSession ? "This session is shared with the main CX2 app." : "New widget sessions stay synced with the app."}
              </div>
              </div>
            </div>
            <button onClick={closeWidget} className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-5 md:px-6">
            {messages.length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <div className="max-w-sm text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-[20px] bg-blue-600 text-white shadow-[0_18px_36px_rgba(37,99,235,0.25)]">
                    <MessageSquare className="h-7 w-7" />
                  </div>
                  <div className="font-SFPro text-xl font-semibold">Start a synced conversation</div>
                  <div className="mt-2 text-sm leading-6 text-slate-500">
                    Anything you start here can be continued later inside the full CX2 app with the same account.
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => {
                  const isBot = message.role === "bot";
                  return (
                    <div key={message.id} className={`flex ${isBot ? "justify-start" : "justify-end"}`}>
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-3 shadow-sm ${
                          isBot
                            ? "bg-white text-slate-700 ring-1 ring-slate-200"
                            : "bg-gradient-to-br from-blue-600 to-blue-700 text-white"
                        }`}
                      >
                        <div
                          className="text-sm leading-7"
                          {...(isBot
                            ? { dangerouslySetInnerHTML: { __html: message.content } }
                            : { children: message.content })}
                        />
                        <div className={`mt-2 text-[11px] ${isBot ? "text-slate-400" : "text-blue-100"}`}>
                          {formatTime(message.timestamp)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {loading && (
                  <div className="flex justify-start">
                    <div className="rounded-2xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-200">
                      <div className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={endRef} />
              </div>
            )}
          </div>

          {error && <div className="px-5 pb-2 text-sm text-red-600">{error}</div>}

          <div className="border-t border-slate-200 bg-white px-4 py-4 md:px-6">
            <div className="flex items-end gap-3">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Type your message..."
                className="min-h-[56px] flex-1 resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:bg-white"
              />
              <button
                onClick={sendMessage}
                disabled={loading || !draft.trim()}
                className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-[0_14px_28px_rgba(37,99,235,0.24)] transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default WidgetApp;
